import React from "react";
import { format } from "date-fns";
import { convertFinalCheckLabel } from "@sscs/report/utils";
import { ReportDetails, SDLCMetadata } from "@sscs/types";
import {
  Button,
  Card,
  CheckLabel,
  KeyValueList,
  LongTextLabel,
  PriorityIcon,
  Toggle,
  Tooltip,
} from "@sscs/ui";
import { EditIcon } from "@sscs/ui/icons";
import {
  PRIORITY_TO_GRADE,
  getClassName,
  getReleaseDateDisplay,
  prettifyLargeCount,
} from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo, useDialog } from "Hooks";
import { FileDetails } from "../types";
import { deriveQualityChange, summarizeComparison } from "../utils";
import CategoryGroups from "./CategoryGroups/CategoryGroups";
import EditSoftwareInfo from "./EditSoftwareInfo/EditSoftwareInfo";
import styles from "./riskAnalysisCard.module.scss";
import ViewReport from "./ViewReport/ViewReport";

const StatusCluster = ({ file }: { file: FileDetails }): React.JSX.Element => {
  const { scan_report } = file.latest_report_summary;
  const { status, scanLevel, isRepro, isDiff } = convertFinalCheckLabel(
    scan_report.checks,
  );
  const totalIssues =
    scan_report.statistics.quality.metrics[
      SDLCMetadata.policy_metrics_t.issues_total_k
    ];

  return (
    <span className={styles["status-cluster"]}>
      <CheckLabel
        status={status}
        scanLevel={scanLevel}
        isLevelCustomized={scan_report.inhibitors.customized}
        isReproducibilityCheck={isRepro}
        isDiffCheck={isDiff}
      />
      <PriorityIcon
        grade={PRIORITY_TO_GRADE[scan_report.statistics.quality.priority]}
        status={status ?? "pass"}
        hasIssues={totalIssues !== 0}
        hasFailedChecks={status === "fail" && isRepro}
      />
    </span>
  );
};

const renderReleaseDate = (file: FileDetails): React.ReactNode => {
  if (file.type === "FILESTREAM") {
    return file.upload_date
      ? format(new Date(file.upload_date), "MMMM d, yyyy")
      : "--";
  }

  return file.software_info.release_date
    ? getReleaseDateDisplay(file.software_info.release_date, "MMMM d, yyyy")
    : "Not released yet";
};

type InfoRow = {
  label: string | ((file: FileDetails) => string);
  value: (file: FileDetails) => React.ReactNode;
};

const INFO_ROWS: InfoRow[] = [
  {
    label: "Product",
    value: ({ software_info }) => (
      <span className={styles["product-value"]}>
        <LongTextLabel label={software_info.product} />
        <LongTextLabel
          label={`\u00a0· ${software_info.version}`}
          title={software_info.version}
          className={styles.version}
        />
      </span>
    ),
  },
  {
    label: "Published by",
    value: (file) => <LongTextLabel label={file.software_info.publisher} />,
  },
  {
    label: "Platform",
    value: ({ software_info }) => (
      <LongTextLabel
        label={`${software_info.platform} · ${software_info.architecture}`}
      />
    ),
  },
  {
    label: "Category",
    value: (file) => <LongTextLabel label={file.software_info.category} />,
  },
  {
    label: "License",
    value: (file) => <LongTextLabel label={file.software_info.license} />,
  },
  {
    label: (file) => (file.type === "FILESTREAM" ? "Uploaded" : "Released"),
    value: renderReleaseDate,
  },
  {
    label: "SBOM",
    value: (file) => {
      const { total_count, verified_count } = file.latest_report_summary.sbom;

      return (
        <LongTextLabel
          label={`${prettifyLargeCount(total_count)} components (${prettifyLargeCount(
            verified_count,
          )} verified)`}
        />
      );
    },
  },
];

const buildInfoList = (file: FileDetails): Record<string, React.ReactNode> =>
  Object.fromEntries(
    INFO_ROWS.map((row): [string, React.ReactNode] => [
      typeof row.label === "string" ? row.label : row.label(file),
      row.value(file),
    ]),
  );

type RiskAnalysisCardProps = {
  id: string;
  file: FileDetails;
  reportDetails: ReportDetails;
  referenceFile?: FileDetails;
  referenceReportDetails?: ReportDetails;
  showViewReportButton?: boolean;
  showEditButton?: boolean;
  showReproReport?: boolean;
  onCardToggle?: () => void;
};

const RiskAnalysisCard = ({
  id,
  file,
  reportDetails,
  referenceFile,
  referenceReportDetails,
  showViewReportButton = true,
  showEditButton = true,
  showReproReport,
  onCardToggle,
}: RiskAnalysisCardProps): React.JSX.Element => {
  const editCurrent = useDialog();
  const editPrevious = useDialog();

  const canEditInfo = useAllowedTo({
    permissions: [
      file.type === "FILESTREAM"
        ? PERMISSION.EDIT_FILE_SOFTWARE_INFO
        : PERMISSION.EDIT_VERSION_SOFTWARE_INFO,
    ],
  });

  const isComparison = referenceFile !== undefined;
  const referenceDeleted = referenceFile?.deleted ?? false;

  const summary = referenceFile
    ? summarizeComparison(
        file.latest_report_summary.scan_report.assessments,
        referenceFile.latest_report_summary.scan_report.assessments,
      )
    : undefined;
  const change = summary ? deriveQualityChange(summary) : undefined;

  const currentReport: ReportDetails = {
    ...reportDetails,
    isRepro: file.type === "VERSION" ? showReproReport : undefined,
  };

  const editButton = (onClick: () => void): React.ReactNode =>
    showEditButton &&
    canEditInfo && (
      <Button
        variant="text"
        isInline
        startIcon={<EditIcon size="xs" />}
        className={styles["edit-all"]}
        onClick={onClick}
      >
        Edit all
      </Button>
    );

  const reproToggle =
    showReproReport !== undefined ? (
      <Toggle
        id={`toggle-repro-${id}`}
        label="View:"
        firstValue="Version"
        secondValue="Repro"
        active={showReproReport}
        onChange={() => onCardToggle?.()}
      />
    ) : undefined;

  const hasStatusInTitle = !isComparison && showReproReport === undefined;

  const titleActions = hasStatusInTitle ? (
    <StatusCluster file={file} />
  ) : (
    reproToggle
  );

  return (
    <Card.Card
      fullWidth={false}
      className={getClassName(styles.card, {
        [styles["card-comparison"]]: isComparison,
      })}
    >
      <Card.Title
        as="h2"
        details={
          change
            ? [
                <span
                  key="change"
                  className={getClassName(
                    styles.pill,
                    styles[`pill-${change}`],
                  )}
                >
                  {change === "worsened" ? "WORSENED" : "IMPROVED"}
                </span>,
              ]
            : undefined
        }
        actions={titleActions}
      >
        Software quality
      </Card.Title>
      <Card.Content
        className={getClassName(styles.content, {
          [styles.compare]: isComparison,
        })}
      >
        {summary && (
          <p className={styles.summary}>
            {summary.worsened} of{" "}
            {summary.worsened + summary.improved + summary.unchanged} categories
            worsened in status against the previous version · {summary.improved}{" "}
            improved · {summary.unchanged} unchanged
          </p>
        )}

        <div
          className={getClassName(styles.info, {
            [styles.blocks]: !hasStatusInTitle,
          })}
        >
          {hasStatusInTitle ? (
            <>
              {editButton(editCurrent.open)}
              <KeyValueList
                list={buildInfoList(file)}
                className={styles["info-list"]}
              />
            </>
          ) : (
            <>
              <section className={styles["current-block"]}>
                <header className={styles["block-header"]}>
                  <span className={styles["block-label"]}>
                    {showReproReport ? "Current: repro" : "Current"}
                  </span>
                  <span className={styles.rule} aria-hidden />
                  <StatusCluster file={file} />
                </header>
                {editButton(editCurrent.open)}
                <KeyValueList
                  list={buildInfoList(file)}
                  className={styles["info-list"]}
                />
              </section>

              {isComparison && (
                <>
                  <span className={styles["vs-gutter"]}>vs</span>

                  <Tooltip
                    title="File has been deleted by another user"
                    disabled={!referenceDeleted}
                  >
                    <section
                      className={getClassName(styles["previous-block"], {
                        [styles.deleted]: referenceDeleted,
                      })}
                    >
                      <header className={styles["block-header"]}>
                        <StatusCluster file={referenceFile} />
                        <span className={styles.rule} aria-hidden />
                        <span className={styles["block-label"]}>Previous</span>
                      </header>
                      {editButton(editPrevious.open)}
                      <KeyValueList
                        list={buildInfoList(referenceFile)}
                        hideKeys
                        className={styles["previous-list"]}
                      />
                    </section>
                  </Tooltip>
                </>
              )}
            </>
          )}
        </div>

        <CategoryGroups
          id={id}
          current={file.latest_report_summary.scan_report.assessments}
          reportDetails={currentReport}
          previous={
            referenceFile?.latest_report_summary.scan_report.assessments
          }
          referenceReportDetails={referenceReportDetails}
          deletedReference={referenceDeleted}
        />
      </Card.Content>

      {showViewReportButton && (
        <Card.Actions>
          <ViewReport id={`view-report-${id}`} report={reportDetails} />
        </Card.Actions>
      )}

      <EditSoftwareInfo
        file={file}
        isOpen={editCurrent.isOpen}
        onClose={editCurrent.close}
      />
      {referenceFile && (
        <EditSoftwareInfo
          file={referenceFile}
          isOpen={editPrevious.isOpen}
          onClose={editPrevious.close}
        />
      )}
    </Card.Card>
  );
};

export default RiskAnalysisCard;
